import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { faTemperature2 } from "@fortawesome/free-solid-svg-icons/faTemperature2";
import { faDroplet } from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { gql } from "graphql-tag";
import { responseField } from "../../../../store/base.graphql";
import { StoreItemActions } from "../../../../store/entities/store-item/store-item.actions";
import { StoreItemEntity } from "../../../../store/entities/store-item/store-item.model";
import { Store } from "@ngrx/store";
import { AppState } from "../../../../store";
import { selectStoreItems } from "../../../../store/entities/store-item/store-item.selectors";
import { Warehouse } from "../../../../store/entities/warehouse-management/warehouse/warehouse.model";
import { Observable, of } from "rxjs";
import { GET_ALL_MSD_WAREHOUSE } from "../../../../store/entities/warehouse-management/warehouse/warehouse.graphql";
import {
  BaseResponse,
  TimeRangesEnum,
} from "../../../../common/interfaces/global-interfaces";
import { DataService } from "../../../../services/data.service";
import { GraphqlService } from "../../../../services/graphql.service";
import { NotificationService } from "../../../../services/notification.service";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { GET_ALL_SENSORS } from "../../../../store/entities/warehouse-management/device/device.graphql";
import { Device } from "../../../../store/entities/warehouse-management/device/device.model";
import { SettingsService } from "../../../../services/settings.service";

export enum AlertTypeEnum {
  MIN_TEMPERATURE = "MIN_TEMPERATURE",
  MAX_TEMPERATURE = "MAX_TEMPERATURE",
  MIN_HUMIDITY = "MIN_HUMIDITY",
  MAX_HUMIDITY = "MAX_HUMIDITY",
}

const GET_WAREHOUSE_ALERT_REPORT = gql`
query GetWarehuseAlertsReport($filtering: AlertsReportsFilteringInputObject) {
  getWarehuseAlertsReport(filtering: $filtering) {
    response {
      ${responseField}
    }
    data {
      reportData
    }
  }
}
`;

@Component({
  selector: "app-alert-report",
  templateUrl: "./alert-report.component.html",
  styleUrl: "./alert-report.component.scss",
})
export class AlertReportComponent implements OnInit {
  @Input() isAllWarehouse: boolean = true;

  private store: Store<AppState> = inject(Store<AppState>);
  private ds: DataService = inject(DataService);
  private apollo: GraphqlService = inject(GraphqlService);
  private notificationService: NotificationService =
    inject(NotificationService);
  private settingsService: SettingsService = inject(SettingsService);

  timeframeTabs: { name: string }[] = [
    { name: "Today" },
    { name: "Yesterday" },
  ];
  @ViewChild("reportContent", { static: false }) reportContent!: ElementRef;
  activeTimeframeTab: string;

  endDate: string;
  startDate: string;

  warehouses$: Observable<Warehouse[]>;
  filtering: {
    warehouse: string;
    sensor: string;
    alertType: string;
    status: string;
    dateTo: string;
    dateFrom: string;
  } = {
    warehouse: null,
    sensor: null,
    alertType: null,
    status: null,
    dateFrom: this.ds.formatDate(new Date()) + "T00:00:00",
    dateTo: this.ds.formatDate(new Date()) + "T23:59:59",
  };

  warehouseSensors$: Observable<Device[]>;

  alertTypes = this.settingsService.enumToArray(AlertTypeEnum);
  reportData: string;

  ngOnInit() {
    this.activeTimeframeTab = "Today";

    if (this.alertTypes?.length) {
      this.filtering.alertType = this.alertTypes[0].value;
    }
    this.filtering.status = "RESOLVED";

    this.store.dispatch(
      StoreItemActions.getStoreItems({
        entity: StoreItemEntity.warehouse,
        query: GET_ALL_MSD_WAREHOUSE,
        filtering: {},
      }),
    );

    this.warehouses$ = this.store.select(
      selectStoreItems(StoreItemEntity.warehouse),
    );
    this.warehouseSensors$ = this.store.select(
      selectStoreItems(StoreItemEntity.warehouseSensor),
    );

    let reportGenerated = false;
    this.warehouses$.subscribe((warehouses) => {
      if (warehouses?.length && !reportGenerated) {
        reportGenerated = true;
        this.filtering.warehouse = warehouses[0].uniqueId;

        this.store.dispatch(
          StoreItemActions.getStoreItems({
            entity: StoreItemEntity.warehouseSensor,
            query: GET_ALL_SENSORS,
            filtering: {
              warehouse: this.filtering.warehouse,
              pageNumber: 1,
              itemsPerPage: 200,
            },
          }),
        );

        this.generateReport();
      }
    });
  }

  onFilterChange(
    evt: string,
    filterType: "timeframe" | "frequency" | "warehouse" | "dateFrom" | "dateTo",
  ) {
    switch (filterType) {
      case "warehouse":
        this.store.dispatch(
          StoreItemActions.getStoreItems({
            entity: StoreItemEntity.warehouseSensor,
            query: GET_ALL_SENSORS,
            filtering: {
              warehouse: evt,
              pageNumber: 1,
              itemsPerPage: 200,
            },
          }),
        );
        this.filtering.sensor = null;
        break;
      case "timeframe":
        this.startDate = null;
        this.endDate = null;
        switch (evt) {
          case "Today":
            this.filtering.dateFrom =
              this.ds.formatDate(new Date()) + "T00:00:00";
            this.filtering.dateTo =
              this.ds.formatDate(new Date()) + "T23:59:59";
            break;
          case "Yesterday":
            let yesterdayDate = new Date();
            yesterdayDate.setDate(yesterdayDate.getDate() - 1);
            this.filtering.dateFrom =
              this.ds.formatDate(yesterdayDate) + "T00:00:00";
            this.filtering.dateTo =
              this.ds.formatDate(yesterdayDate) + "T23:59:59";
            break;
        }
        break;
      case "dateFrom":
        this.filtering.dateFrom =
          this.ds.formatDate(this.startDate) + "T00:00:00";
        break;
      case "dateTo":
        this.filtering.dateTo = this.ds.formatDate(this.endDate) + "T23:59:59";
        break;
    }
  }

  async generateReport() {
    try {
      const response: any = await this.apollo.query({
        query: GET_WAREHOUSE_ALERT_REPORT,
        variables: {
          filtering: this.filtering,
        },
      });

      const result: BaseResponse<{ reportData: string }> = Object.values(
        Object.values(response)[0],
      )[0];

      if (result?.response?.status) {
        this.reportData = result?.data?.reportData;
      } else {
        throw Error(result.response.message);
      }
    } catch (e) {
      this.notificationService.errorMessage(e.message);
    }
  }

  protected readonly faDroplet = faDroplet;
  protected readonly faCalendar = faCalendar;
  protected readonly Math = Math;
  protected readonly Array = Array;
  protected readonly of = of;
}
